import { useCallback, useState } from 'react';
import { CompactTableData } from '../types';

export const useExpandableRows = <T extends CompactTableData>({ data }: { data: T[] }) => {
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [allExpandedByDefault, setAllExpandedByDefault] = useState(false);

  const expandAllRows = useCallback(() => {
    setAllExpandedByDefault(true);
    setExpandedRows({});
  }, []);

  const hideAllRows = useCallback(() => {
    setAllExpandedByDefault(false);
    setExpandedRows({});
  }, []);

  const toggleAllRowsExpanded = useCallback(() => {
    if (allExpandedByDefault) {
      hideAllRows();
    } else {
      expandAllRows();
    }
  }, [expandAllRows, hideAllRows, allExpandedByDefault]);

  /**
   * This function is used to handle the expansion of a single row.
   * It updates the expandedRows state based on the current state of the row and the allExpandedByDefault flag.
   * The allExpandedByDefault state determines the state for the newly added rows when an infinite query is used.
   * If a row is being collapsed while the default state is expanded, it sets the default state to collapsed but preserves the expanded state of current rows.
   * Conversely, if a row is being expanded while the default state is collapsed, it checks if all rows are now expanded and if so, it sets the default state to expanded and clears the individual row states.
   */
  const handleRowExpand = useCallback(
    (item: T, isExpanded: boolean) => {
      setExpandedRows((prev) => {
        let newExpandedRows = { ...prev, [item.id]: !isExpanded };
        const isExpandedNow = !isExpanded;
        if (!isExpandedNow && allExpandedByDefault) {
          newExpandedRows = data.reduce((acc, r) => ({ ...acc, [r.id]: newExpandedRows[r.id] ?? true }), {});
          setAllExpandedByDefault(false);
        } else if (isExpandedNow && !allExpandedByDefault) {
          const allRowsExpanded = data.every((r) => r.id === item.id || newExpandedRows[r.id] === true);
          if (allRowsExpanded) {
            newExpandedRows = {};
            setAllExpandedByDefault(true);
          }
        }
        return newExpandedRows;
      });
    },
    [setExpandedRows, allExpandedByDefault, setAllExpandedByDefault, data]
  );

  return {
    expandedRows,
    setExpandedRows,
    allExpandedByDefault,
    setAllExpandedByDefault,
    toggleAllRowsExpanded,
    handleRowExpand,
  };
};
